import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { ApplicationContextService } from './application-context.service';
import { Observable, ReplaySubject, Subject, distinctUntilChanged, filter, firstValueFrom, map } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { ApplicationDto } from './application.do';
import { protectedResources } from '../../auth-config';

export abstract class BaseSignalrService {
  protected appId: Observable<number>;
  protected appGroupId: Observable<number>;
  protected appIdSnapshot: number;
  protected appGroupIdSnapshot: number;
  protected app: Observable<ApplicationDto>;
  protected tabActivated: Observable<number>;
  protected tabDeactivated: Observable<void>;
  protected tabActive: boolean = true;
  protected get tabInactive() {
    return !this.tabActive;
  };

  private connection: HubConnection;
  private readonly account: AccountInfo;
  private readonly listeners: Map<string, ReplaySubject<unknown>>
    = new Map<string, ReplaySubject<unknown>>([]);
  private endpoint: Observable<string>;
  private endpointSnapshot: string;

  constructor(
    protected applicationContextService: ApplicationContextService,
    protected authService: MsalService,
    private document: Document) {
    this.appId = this.applicationContextService.applicationIdObservable;
    this.appGroupId = this.applicationContextService.applicationGroupIdObservable;

    this.appId.subscribe(id => this.appIdSnapshot = id);
    this.appGroupId.subscribe(id => this.appGroupIdSnapshot = id);

    this.app = this.applicationContextService.applicationObservable;

    this.account = this.authService.instance.getActiveAccount();

    const visible = new Subject<boolean>();
    let lastChange = new Date();
    this.document.addEventListener("visibilitychange", () => {
      this.tabActive = this.document.visibilityState == 'visible';
      visible.next(this.tabActive);
      lastChange = new Date();
    });

    this.tabActivated = visible
      .pipe(
        filter(i => i),
        map(i => new Date().getTime() - lastChange.getTime()));

    this.tabDeactivated = visible
      .pipe(
        filter(i => !i),
        map(i => null));

    this.buildConnection();
  }

  protected init() {
    this.endpoint = this.getEndpoint()
      .pipe(distinctUntilChanged());
    this.endpoint.subscribe(async endpoint => {
      this.endpointSnapshot = endpoint;
      switch (this.connection.state) {
        case HubConnectionState.Connected:
        case HubConnectionState.Connecting:
        case HubConnectionState.Disconnecting:
        case HubConnectionState.Reconnecting:
          await this.connection.stop();
          break;
        case HubConnectionState.Disconnected:
          if (endpoint) {
            await this.start(endpoint);
          }
          break;
      }
    });
  }

  protected abstract registerListeners(register: <T>(method: string) => Observable<T>);

  /**
  * Invoked when the connection is established.
  *
  * @param cancelationToken - an observable that will complete once the connection is dropped
  */
  protected abstract connectionStarted(cancelationToken: Subject<void>): void;

  /**
   * Returns an observable of string which is the endpoint to connect to.
   *
   * @returns Observable\<string\> - the connection is recreated on each change. If null is emitted, the connection will be dropped.
   */
  protected abstract getEndpoint(): Observable<string>;

  protected invoke<T>(method: string, ...args: unknown[]): Promise<T> {
    return this.connection.invoke(method, ...args);
  }

  private buildConnection() {
    this.connection = new HubConnectionBuilder()
      .withUrl('this is really set later before reconnecting', {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: this.tokenFactory
      })
      .withAutomaticReconnect()
      .build();

    this.registerListeners(m => this.addListener(m));
    const disconnected = new Subject<void>();
    this.connection.onreconnected(() => this.connectionStarted(disconnected));
    this.connection.onclose(() => {
      for (const method of this.listeners.keys()) {
        const observable = this.listeners.get(method);
        this.connection.off(method);
        observable.complete();
        disconnected.next();
        disconnected.complete();
      }

      this.listeners.clear();
      this.buildConnection();

      if (this.endpointSnapshot) {
        this.start(this.endpointSnapshot);
      }
    });
  }

  private async start(endpoint: string): Promise<void> {
    for (const method of this.listeners.keys()) {
      const observable = this.listeners.get(method);
      this.connection.on(method, async (data) => {
        observable.next(data);
      });
    }
    this.connection.baseUrl = new HubConnectionBuilder().withUrl(endpoint).build().baseUrl;
    const disconnected = new Subject<void>();
    this.connection.onclose(() => {
      disconnected.next();
      disconnected.complete();
    });
    return this.connection.start().then(() => this.connectionStarted(disconnected));
  }

  private addListener<T>(method: string): Observable<T> {
    let observable = this.listeners.get(method);

    if (!observable) {
      observable = new ReplaySubject<T>(1);
      this.listeners.set(method, observable);
    }

    return observable.asObservable() as Observable<T>;
  }

  private get tokenFactory(): () => Promise<string> {
    return () => firstValueFrom(this.authService.acquireTokenSilent({
      account: this.account, scopes: protectedResources.datexApplicationApi.scopes
    })).then(t => t.accessToken);
  }
}